import { createAuthorisedApiRequest } from "../helpers/create-authorised-request";
import { fetchWrapper } from "../helpers/fetch-wrapper";
import { associateMatterIdInConversationCache } from "./conversation-service";
import { getEnvPropertyName, getMatterIdPropertyName, getRegionPropertyName } from "./mailbox-service";
import { ensureConfig } from "./configuration-service";
import { IAttachment, IMailItem } from "../mailitem/mail-item-types";
import { MetricPayload, UNKNOWN_METRICS_VALUE } from "../models/metric-model";
import { getDiagnostics } from "../helpers/DiagnosticsHelper";
import { ViewType } from "../components/Search/SearchPresentational";
import { v4 } from "uuid";

type TAttachmentPayload = {
  MatterId: string;
  MailItemId: string;
  AttachmentId: string;
  AttachmentNameOverride: string | null;
  PreferredMatterFolderId: string | null;
};

type TAssignPayload = {
  FileId: string;
  MatterId: string;
  MailItemId: string;
  FileNameOverride: string | null;
  Attachments: TAttachmentPayload[] | null;
  PreferredMatterFolderId: string | null;
};

const assignDraftToMatter = async (mailItem: IMailItem, matterId: string): Promise<{ err?: unknown }> => {
  const validateResult = await validateAssignToMatter(mailItem.itemId, matterId, [], null);
  if (validateResult.err) {
    return validateResult;
  }
  await mailItem.setCustomProperty(getMatterIdPropertyName(), matterId);
  associateMatterIdInConversationCache(mailItem.conversationId, matterId);

  console.warn(
    "Category assignment is not supported for draft mail items at this stage. This will be handled when the mail item is sent."
  );
  // TODO: Reinstate if microsoft ever allows adding category to draft mail items.
  // await mailItem.addProductCategory();
  const config = ensureConfig();
  console.log(`Assigning property ${getRegionPropertyName()} as ${config.region.code}`);
  await mailItem.setCustomProperty(getRegionPropertyName(), config.region.code);
  console.log(`Assigning property ${getEnvPropertyName()} as ${config.environment}`);
  await mailItem.setCustomProperty(getEnvPropertyName(), config.environment);
  return {};
};

const unAssignDraftFromMatter = async (mailItem: IMailItem) => {
  await mailItem.setCustomProperty(getMatterIdPropertyName(), null);
  // this needs to be done in case the user manually disassociates and navs away and back
  associateMatterIdInConversationCache(mailItem.conversationId, null);
  try {
    await mailItem.removeProductCategory();
  } catch {
    // pass fails in OWA
  }
};

const validateAssignToMatter = async (
  mailItemId: string,
  matterId: string,
  attachments: IAttachment[],
  targetFolderId: string | null
): Promise<{ result?: unknown; err?: unknown }> => {
  const params: TAssignPayload = {
    FileId: v4(),
    MatterId: matterId,
    FileNameOverride: null,
    MailItemId: mailItemId,
    Attachments:
      attachments?.map<TAttachmentPayload>((a) => ({
        AttachmentId: a.id,
        MatterId: matterId,
        MailItemId: mailItemId,
        AttachmentNameOverride: null,
        PreferredMatterFolderId: targetFolderId,
      })) || [],
    PreferredMatterFolderId: targetFolderId || "",
  };
  const { url, requestInit } = await createAuthorisedApiRequest("ValidateMatterAssignUrl", params);
  const result = await fetchWrapper(url, requestInit);
  return result;
};

const assignEmailToMatter = async (
  mailItem: IMailItem,
  matterId: string,
  attachments: IAttachment[],
  targetFolderId: string | null,
  searchViewType: ViewType
): Promise<{ result?: unknown; err?: unknown }> => {
  const params: TAssignPayload = {
    FileId: v4(),
    MatterId: matterId,
    FileNameOverride: null,
    MailItemId: mailItem.itemId,
    Attachments:
      attachments?.map<TAttachmentPayload>((a) => ({
        AttachmentId: a.id,
        MatterId: matterId,
        MailItemId: mailItem.itemId,
        AttachmentNameOverride: null,
        PreferredMatterFolderId: targetFolderId,
      })) || [],
    PreferredMatterFolderId: targetFolderId || "",
  };
  const { url, requestInit } = await createAuthorisedApiRequest("MatterAssignUrl", params);
  const result = await fetchWrapper(url, requestInit);

  if (result.result && !result.err) {
    await mailItem.setCustomProperty(getMatterIdPropertyName(), matterId);
    associateMatterIdInConversationCache(mailItem.conversationId, matterId);
    try {
      await mailItem.addProductCategory();
    } catch {
      // pass this fails in OWA
    }

    const hasAttachments = attachments?.length > 0 || false;
    const metricName = "outlook_addin_assign";

    try {
      const diagnostics = getDiagnostics();

      let source: string;

      if (searchViewType === "RECENT") {
        source = "Recent matters";
      } else if (searchViewType === "SEARCH") {
        source = "Search";
      } else if (searchViewType === "DEFAULT") {
        source = "Assigned tile";
      } else {
        source = UNKNOWN_METRICS_VALUE;
      }

      const metricPayload: MetricPayload = {
        Metric: {
          Name: metricName,
          Timestamp: new Date().toISOString(), // Current UTC time
          Dimensions: {
            Platform: diagnostics?.platform || UNKNOWN_METRICS_VALUE,
            Version: diagnostics?.version || UNKNOWN_METRICS_VALUE,
            HostName: diagnostics?.hostName || UNKNOWN_METRICS_VALUE,
            HostVersion: diagnostics?.hostVersion || UNKNOWN_METRICS_VALUE,
            Source: source,
            HasAttachments: hasAttachments.toString(),
            Product: "",
          },
        },
      };

      const { url, requestInit } = await createAuthorisedApiRequest("AddMetricUrl", metricPayload);
      const result = await fetchWrapper(url, requestInit);

      if (result.err) {
        console.error(`Add metric call returned an error when saving ${metricName} metric: `, result.err);
      }
    } catch (error) {
      console.error(`Error occurred when saving ${metricName} metric: `, error);
    }
  }

  return result;
};

type TUnAssignPayload = {
  MailItemId: string;
  MatterId: string;
};

const unAssignEmailFromMatter = async (
  mailItem: IMailItem,
  matterId: string
): Promise<{ result?: unknown; err?: unknown }> => {
  const params: TUnAssignPayload = {
    MailItemId: mailItem.itemId,
    MatterId: matterId,
  };

  const { url, requestInit } = await createAuthorisedApiRequest("MatterUnAssignUrl", params);
  const result = await fetchWrapper(url, requestInit);

  if (result.result && !result.err) {
    await mailItem.setCustomProperty(getMatterIdPropertyName(), null);
    associateMatterIdInConversationCache(mailItem.conversationId, null);

    // TODO: Need to support the different product tier categories.
    try {
      await mailItem.removeProductCategory();
    } catch {
      // pass
    }

    const metricName = "outlook_addin_unassign";

    try {
      const diagnostics = getDiagnostics();

      const metricPayload: MetricPayload = {
        Metric: {
          Name: metricName,
          Timestamp: new Date().toISOString(), // Current UTC time
          Dimensions: {
            Platform: diagnostics?.platform || UNKNOWN_METRICS_VALUE,
            Version: diagnostics?.version || UNKNOWN_METRICS_VALUE,
            HostName: diagnostics?.hostName || UNKNOWN_METRICS_VALUE,
            HostVersion: diagnostics?.hostVersion || UNKNOWN_METRICS_VALUE,
            Product: "",
          },
        },
      };

      const { url, requestInit } = await createAuthorisedApiRequest("AddMetricUrl", metricPayload);
      const result = await fetchWrapper(url, requestInit);

      if (result.err) {
        console.error(`Add metric call returned an error when saving ${metricName} metric: `, result.err);
      }
    } catch (error) {
      console.error(`Error occurred when saving ${metricName} metric: `, error);
    }
  }

  return result;
};

export const getMatterAssigner = () => ({
  assignDraftToMatter,
  assignEmailToMatter,
  unAssignDraftFromMatter,
  unAssignEmailFromMatter,
});

export type TMatterAssigner = ReturnType<typeof getMatterAssigner>;
